/** Provides shared pixel types plus brush sizing, coordinate and keyboard-target helpers for the shared image editor. */
import {Coordinates} from 'advanced-cropper/types';

/** Native-resolution RGBA pixels; structurally compatible with the browser's ImageData, which jsdom lacks. */
export interface PixelBuffer {
   width: number;
   height: number;
   data: Uint8ClampedArray;
}

/** An axis-aligned rectangle; for pixel bounds, right and bottom are exclusive. */
export interface Bounds {
   left: number;
   top: number;
   right: number;
   bottom: number;
}

export interface Point {
   x: number;
   y: number;
}

/** Input types whose keyboard input is not text, so editor shortcuts may still act while they have focus. */
const NON_TEXT_INPUT_TYPES = ['range', 'checkbox', 'radio', 'button', 'submit', 'reset'];

/** Converts the brush-size percentage into a diameter relative to the image's shorter edge. */
export const getBrushDiameter = (width: number, height: number, percentage: number): number =>
   Math.max(1, Math.min(width, height) * percentage / 100);

/** Reports whether a viewport point lies within the displayed image canvas. */
export const isPointWithinBounds = (x: number, y: number, bounds: Bounds): boolean =>
   x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;

/** Normalises a rotation in degrees to the range 0–359, so −90 becomes 270. */
export const normalizeRotation = (rotation: number): number => ((rotation % 360) + 360) % 360;

/** Maps a viewport point onto native image coordinates for the editor's quarter-turn rotations. */
export const mapPointToImage = (
   x: number,
   y: number,
   bounds: Bounds,
   imageWidth: number,
   imageHeight: number,
   rotation: number
): Point => {
   const normalizedX = (x - bounds.left) / (bounds.right - bounds.left);
   const normalizedY = (y - bounds.top) / (bounds.bottom - bounds.top);
   const normalizedRotation = normalizeRotation(rotation);

   if (normalizedRotation === 90) {
      return {x: normalizedY * imageWidth, y: (1 - normalizedX) * imageHeight};
   }
   if (normalizedRotation === 180) {
      return {x: (1 - normalizedX) * imageWidth, y: (1 - normalizedY) * imageHeight};
   }
   if (normalizedRotation === 270) {
      return {x: (1 - normalizedY) * imageWidth, y: normalizedX * imageHeight};
   }
   return {x: normalizedX * imageWidth, y: normalizedY * imageHeight};
};

/** Maps native image bounds into the cropper's rotated coordinate space; the inverse of mapPointToImage. */
export const mapImageBoundsToCropper = (
   bounds: Bounds,
   imageWidth: number,
   imageHeight: number,
   rotation: number
): Coordinates => {
   const width = bounds.right - bounds.left;
   const height = bounds.bottom - bounds.top;
   const normalizedRotation = normalizeRotation(rotation);

   if (normalizedRotation === 90) {
      return {left: imageHeight - bounds.bottom, top: bounds.left, width: height, height: width};
   }
   if (normalizedRotation === 180) {
      return {left: imageWidth - bounds.right, top: imageHeight - bounds.bottom, width, height};
   }
   if (normalizedRotation === 270) {
      return {left: bounds.top, top: imageWidth - bounds.right, width: height, height: width};
   }
   return {left: bounds.left, top: bounds.top, width, height};
};

/** Reports whether keyboard input on the target is text entry that editor shortcuts must not hijack. */
export const isTextEntryElement = (target: EventTarget | null): boolean => {
   if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
      return true;
   }
   if (target instanceof HTMLInputElement) {
      return !NON_TEXT_INPUT_TYPES.includes(target.type);
   }
   return target instanceof HTMLElement && target.isContentEditable === true;
};
